import React, { useMemo } from 'react'
import type { EChartsOption } from 'echarts'
import EChartsCanvas from '../core/EChartsCanvas'
import { useAnalyticsTheme } from '../theme/useAnalyticsTheme'
import { buildAnalyticsAxisTooltip, buildAreaGradient } from '../theme/echartsTheme'

export type AnalyticsLineSeries = {
 name: string
 data: number[]
 color?: string
 showArea?: boolean
}

export type AnalyticsLineChartProps = {
 categories: string[]
 /** Single-series shorthand */
 data?: number[]
 /** Multi-series mode */
 series?: AnalyticsLineSeries[]
 height?: number
 color?: string
 valueFormatter?: (value: number) => string
 seriesName?: string
 showArea?: boolean
 showLegend?: boolean
 loading?: boolean
 className?: string
}

const AnalyticsLineChart: React.FC<AnalyticsLineChartProps> = ({
 categories,
 data,
 series,
 height = 280,
 color,
 valueFormatter,
 seriesName = '',
 showArea = true,
 showLegend = false,
 loading = false,
 className,
}) => {
 const { mode, colors, baseOption, rtl } = useAnalyticsTheme()

 const resolvedSeries = useMemo<AnalyticsLineSeries[]>(() => {
  if (series?.length) return series
  return [{ name: seriesName, data: data ?? [], color, showArea }]
 }, [series, seriesName, data, color, showArea])

 const empty =
  categories.length === 0 ||
  resolvedSeries.every((s) => s.data.every((v) => !Number.isFinite(v) || v === 0))

 const singleCategory = categories.length === 1

 const option = useMemo<EChartsOption>(() => {
  const baseGrid =
   typeof baseOption.grid === 'object' && !Array.isArray(baseOption.grid)
    ? baseOption.grid
    : {}
  const gridBottom = showLegend ? 48 : typeof baseGrid.bottom === 'number' ? baseGrid.bottom : 32

  const tooltipBase =
   typeof baseOption.tooltip === 'object' && !Array.isArray(baseOption.tooltip)
    ? baseOption.tooltip
    : {}

  const tooltip: EChartsOption['tooltip'] = rtl
   ? buildAnalyticsAxisTooltip(tooltipBase as Record<string, unknown>, valueFormatter)
   : {
      ...tooltipBase,
      trigger: 'axis' as const,
      axisPointer: { type: 'cross' as const, crossStyle: { opacity: 0.35 } },
      valueFormatter: valueFormatter
       ? (v) => valueFormatter(Number(Array.isArray(v) ? v[0] : v ?? 0))
       : undefined,
     } as EChartsOption['tooltip']

  return {
   backgroundColor: 'transparent',
   color: [...colors.palette],
   grid: { ...baseGrid, bottom: gridBottom, containLabel: true },
   textStyle: baseOption.textStyle,
   legend: showLegend
    ? {
       bottom: 0,
       left: 'center',
       itemWidth: 10,
       itemHeight: 10,
       textStyle: { color: colors.label, fontSize: 11 },
      }
    : undefined,
   tooltip,
   xAxis: {
    type: 'category',
    data: categories,
    boundaryGap: false,
    ...(rtl ? { inverse: true } : {}),
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: colors.label, fontSize: 11, interval: 'auto', hideOverlap: true },
   },
   yAxis: {
    type: 'value',
    ...(rtl ? { position: 'right' as const } : {}),
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
     color: colors.label,
     fontSize: 11,
     formatter: valueFormatter ? (v: number) => valueFormatter(Number(v)) : undefined,
    },
    splitLine: { show: false },
   },
   series: resolvedSeries.map((s, i) => {
    const lineColor = s.color ?? colors.palette[i % colors.palette.length]
    const areaEnabled = s.showArea ?? showArea

    if (singleCategory) {
     return {
      name: s.name,
      type: 'bar' as const,
      barMaxWidth: 24,
      data: s.data.map((value) => ({
       value,
       itemStyle: { color: lineColor, borderRadius: [6, 6, 0, 0] },
      })),
      emphasis: { itemStyle: { shadowBlur: 8, shadowColor: 'rgba(0,0,0,0.12)' } },
     }
    }

    return {
     name: s.name,
     type: 'line',
     smooth: true,
     symbol: 'circle',
     symbolSize: 6,
     showSymbol: categories.length <= 14,
     lineStyle: { width: 2.5, color: lineColor },
     itemStyle: { color: lineColor },
     areaStyle: areaEnabled ? { color: buildAreaGradient(mode, lineColor) } : undefined,
     emphasis: { focus: 'series', scale: true },
     data: s.data,
     large: s.data.length > 200,
     largeThreshold: 200,
    }
   }),
  }
 }, [
  baseOption,
  categories,
  colors.label,
  colors.palette,
  mode,
  resolvedSeries,
  rtl,
  singleCategory,
  showArea,
  showLegend,
  valueFormatter,
 ])

 return (
  <EChartsCanvas
   option={option}
   height={height}
   className={className}
   loading={loading}
   empty={empty}
   themeKey={`line-${mode}-${rtl ? 'rtl' : 'ltr'}-${resolvedSeries.length}`}
  />
 )
}

export default AnalyticsLineChart
